import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useTranslation } from '../../hooks/useTranslation';
import {
  AdminPageHeader,
  AdminTable,
  AdminEmptyState,
  type AdminTableColumn,
} from '../../components/admin';
import type { AdminUserListItem } from '@flymind/shared';

interface PaginatedUsers {
  items: AdminUserListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const PLAN_COLORS: Record<string, string> = {
  free: 'bg-gray-100 text-gray-700',
  premium: 'bg-blue-100 text-blue-700',
  unlimited: 'bg-purple-100 text-purple-700',
};

const ROLE_COLORS: Record<string, string> = {
  user: 'bg-gray-100 text-gray-700',
  admin: 'bg-amber-100 text-amber-700',
  super_admin: 'bg-red-100 text-red-700',
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { adminApi } = useAdmin();
  const { t, locale } = useTranslation();
  const dateLocale = locale === 'en' ? 'en-US' : 'it-IT';

  const PLAN_LABELS: Record<string, string> = {
    free: t('admin.common.planFree'),
    premium: t('admin.common.planPremium'),
    unlimited: t('admin.common.planUnlimited'),
  };

  const ROLE_LABELS: Record<string, string> = {
    user: t('admin.common.roleUser'),
    admin: t('admin.common.roleAdmin'),
    super_admin: t('admin.common.roleSuperAdmin'),
  };

  const [data, setData] = useState<PaginatedUsers | null>(null);
  const [loading, setLoading] = useState(true);
  const [banningId, setBanningId] = useState<string | null>(null);

  // Read filters from URL search params
  const search = searchParams.get('search') || '';
  const planFilter = searchParams.get('plan') || '';
  const roleFilter = searchParams.get('role') || '';
  const bannedFilter = searchParams.get('banned') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        // Reset page when changing filters
        if (key !== 'page') {
          next.delete('page');
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '20');
      if (search) params.set('search', search);
      if (planFilter) params.set('plan', planFilter);
      if (roleFilter) params.set('role', roleFilter);
      if (bannedFilter) params.set('banned', bannedFilter);

      const result = await adminApi.get<PaginatedUsers>(
        `/users?${params.toString()}`,
      );
      setData(result);
    } catch (err) {
      console.error('Errore nel caricamento utenti:', err);
    } finally {
      setLoading(false);
    }
  }, [adminApi, page, search, planFilter, roleFilter, bannedFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleBanToggle = async (
    e: React.MouseEvent,
    userId: string,
    currentlyBanned: boolean,
  ) => {
    e.stopPropagation();
    if (
      !confirm(
        currentlyBanned
          ? t('admin.users.confirmUnban')
          : t('admin.users.confirmBan'),
      )
    ) {
      return;
    }

    setBanningId(userId);
    try {
      const action = currentlyBanned ? 'unban' : 'ban';
      await adminApi.post(`/users/${userId}/${action}`);
      await fetchUsers();
    } catch (err) {
      console.error('Errore ban/unban:', err);
    } finally {
      setBanningId(null);
    }
  };

  const columns: AdminTableColumn<AdminUserListItem>[] = [
    {
      key: 'name',
      label: t('admin.users.colName'),
      render: (row) => (
        <span className="font-medium">{row.name || '-'}</span>
      ),
    },
    {
      key: 'email',
      label: t('admin.users.colEmail'),
      render: (row) => (
        <span className="text-text-secondary">{row.email}</span>
      ),
    },
    {
      key: 'plan',
      label: t('admin.users.colPlan'),
      render: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${PLAN_COLORS[row.plan] || ''}`}
        >
          {PLAN_LABELS[row.plan] || row.plan}
        </span>
      ),
    },
    {
      key: 'role',
      label: t('admin.users.colRole'),
      render: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${ROLE_COLORS[row.role] || ''}`}
        >
          {ROLE_LABELS[row.role] || row.role}
        </span>
      ),
    },
    {
      key: 'banned',
      label: t('admin.users.colStatus'),
      render: (row) => (
        <span className="inline-flex items-center gap-1.5 text-sm">
          <span
            className={`inline-block h-2 w-2 rounded-full ${row.banned ? 'bg-red-500' : 'bg-green-500'}`}
          />
          {row.banned ? t('admin.common.statusBanned') : t('admin.common.statusActive')}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: t('admin.users.colCreated'),
      render: (row) =>
        new Date(row.createdAt).toLocaleDateString(dateLocale, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
    },
    {
      key: 'actions',
      label: t('admin.users.colActions'),
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/users/${row.id}`);
            }}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            {t('admin.common.details')}
          </button>
          <button
            onClick={(e) => handleBanToggle(e, row.id, row.banned)}
            disabled={banningId === row.id}
            className={`text-sm font-medium ${
              row.banned
                ? 'text-green-600 hover:text-green-700'
                : 'text-red-600 hover:text-red-700'
            } disabled:opacity-50`}
          >
            {banningId === row.id
              ? '...'
              : row.banned
                ? t('admin.users.unban')
                : t('admin.users.ban')}
          </button>
        </div>
      ),
    },
  ];

  const totalPages = data?.totalPages ?? 0;

  return (
    <div>
      <AdminPageHeader
        title={t('admin.users.title')}
        subtitle={
          data ? t('admin.users.totalUsers').replace('{count}', String(data.total)) : undefined
        }
      />

      {/* Search and Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="text"
          placeholder={t('admin.users.searchPlaceholder')}
          value={search}
          onChange={(e) => setParam('search', e.target.value)}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:max-w-xs"
        />

        <select
          value={planFilter}
          onChange={(e) => setParam('plan', e.target.value)}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">{t('admin.users.filterAllPlans')}</option>
          <option value="free">{t('admin.common.planFree')}</option>
          <option value="premium">{t('admin.common.planPremium')}</option>
          <option value="unlimited">{t('admin.common.planUnlimited')}</option>
        </select>

        <select
          value={roleFilter}
          onChange={(e) => setParam('role', e.target.value)}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">{t('admin.users.filterAllRoles')}</option>
          <option value="user">{t('admin.common.roleUser')}</option>
          <option value="admin">{t('admin.common.roleAdmin')}</option>
          <option value="super_admin">{t('admin.common.roleSuperAdmin')}</option>
        </select>

        <select
          value={bannedFilter}
          onChange={(e) => setParam('banned', e.target.value)}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">{t('admin.users.filterAllStatuses')}</option>
          <option value="false">{t('admin.common.statusActive')}</option>
          <option value="true">{t('admin.common.statusBanned')}</option>
        </select>
      </div>

      {/* Table */}
      {!loading && data && data.items.length === 0 ? (
        <AdminEmptyState
          title={t('admin.users.emptyTitle')}
          description={t('admin.users.emptyDescription')}
        />
      ) : (
        <AdminTable
          columns={columns}
          data={data?.items ?? []}
          loading={loading}
          onRowClick={(row) =>
            navigate(`/admin/users/${(row as unknown as AdminUserListItem).id}`)
          }
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-secondary">
            {t('admin.common.pageOf').replace('{page}', String(page)).replace('{total}', String(totalPages))} ({t('admin.common.resultsCount').replace('{count}', String(data?.total ?? 0))})
          </p>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setParam('page', String(page - 1))}
              className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.common.previous')}
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setParam('page', String(page + 1))}
              className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
